
import React, { useState, useEffect, useRef, useMemo } from 'react';
import IntegrationsComponent from '../../components/Integrations/IntegrationsComponent';
import { Language, AppConfig, IntegrationApp } from '../../types';
import { generateResponse, generateAudio } from '../../services/geminiService';
import { audioService } from '../../services/audioService';
import { IntegrationsService } from '../../api';
import { useDataTemplate } from '../../hooks/useDataTemplate';

interface IntegrationsTemplateProps {
  language: Language;
  config: AppConfig;
  apps: IntegrationApp[]; // Deprecated
  setApps: React.Dispatch<React.SetStateAction<IntegrationApp[]>>; // Deprecated
}

const MOCK_CALLER_SCRIPTS = [
  "Hello, I am calling about the delivery. Can you confirm your address?",
  "We have a meeting scheduled for tomorrow at 10 AM, is that still good?",
  "I am waiting outside, the gate is locked. Can you open it?",
  "Did you receive the email I sent yesterday regarding the project?"
];

const MOCK_ROUTE_EVENTS = [
  "Starting route. Heavy traffic reported on King Fahd Road.",
  "Speed camera (Saher) ahead in 500 meters. Slow down.",
  "Turn right at the next signal.",
  "You are passing Kingdom Centre on your left.",
  "New message received: 'Where are you?'.",
  "Rerouting to avoid congestion...",
  "Destination is on your right. You have arrived."
];

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const IntegrationsTemplate: React.FC<IntegrationsTemplateProps> = ({ language, config }) => {
  // --- Data Gateway ---
  const { data: apps, setData: setApps, isLoading } = useDataTemplate<IntegrationApp[]>(IntegrationsService.getAll);

  const [selectedApp, setSelectedApp] = useState<IntegrationApp | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'settings' | 'live'>('dashboard');
  const [isSimulating, setIsSimulating] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // -- Real Location & Navigation State --
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [destinationInput, setDestinationInput] = useState('');
  const [activeDestination, setActiveDestination] = useState('');
  const [aiMapResponse, setAiMapResponse] = useState('');
  const [navStepIndex, setNavStepIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  
  // -- Call State --
  const [callStatus, setCallStatus] = useState<'incoming' | 'active' | 'ended'>('incoming');
  const [callDuration, setCallDuration] = useState(0);
  const [callTranscript, setCallTranscript] = useState<{original: string, translated: string, isUser: boolean}[]>([]);
  
  // -- Shared AI State --
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const isSimulatingRef = useRef(false);
  const locationWatchId = useRef<number | null>(null);

  const visibleApps = useMemo(() => apps?.filter(a => !a.hidden) || [], [apps]);

  // Sync to local for Local Mode
  useEffect(() => {
      if (apps) localStorage.setItem('lahja_integrations', JSON.stringify(apps));
  }, [apps]);

  useEffect(() => { isSimulatingRef.current = isSimulating; }, [isSimulating]);

  useEffect(() => {
    let interval: any;
    if (callStatus === 'active') {
      interval = setInterval(() => setCallDuration(p => p + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [callStatus]);

  useEffect(() => {
    if (selectedApp?.id === 'google-maps') {
      if (!navigator.geolocation) {
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
           setUserLocation({
             lat: position.coords.latitude,
             lng: position.coords.longitude
           });
        },
        (err) => console.warn("Location fetch failed", err)
      );

      if (activeTab === 'live' && isSimulating) {
        locationWatchId.current = navigator.geolocation.watchPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (error) => console.warn("GPS Signal Lost"),
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
        );
      }
    }

    return () => {
      if (locationWatchId.current !== null) navigator.geolocation.clearWatch(locationWatchId.current);
    };
  }, [selectedApp, activeTab, isSimulating]);

  const speakResponse = async (text: string) => {
      if (!text) return;
      setIsAiSpeaking(true);
      try {
          const voice = selectedApp?.config.voice || config.voice;
          const audio = await generateAudio(text, voice, config.apiKey);
          if (audio) await audioService.playPCM(audio, config.speechSpeed);
      } catch (e) {
          console.error(e);
      } finally {
          setIsAiSpeaking(false);
      }
  };

  const handleNavigationEvent = async () => {
      if (!isNavigating || isAiSpeaking) return;
      
      const event = MOCK_ROUTE_EVENTS[navStepIndex];
      const dialect = selectedApp?.config.dialect || config.dialect;
      
      const prompt = `Role: Funny Saudi Navigation Assistant. Current Event: "${event}". Destination: "${activeDestination}". Task: Give direction in Saudi ${dialect}. Tone: Helpful/Informal. Keep under 15 words.`;

      const response = await generateResponse(prompt, dialect as any, [], [], language, config.apiKey);
      setAiMapResponse(response);
      await speakResponse(response);

      if (navStepIndex < MOCK_ROUTE_EVENTS.length - 1) {
          setNavStepIndex(prev => prev + 1);
      } else {
          setIsNavigating(false);
      }
  };

  const startNavigation = () => {
      if (!destinationInput.trim()) return;
      setActiveDestination(destinationInput);
      setActiveTab('live');
      setIsSimulating(true);
      setIsNavigating(true);
      setNavStepIndex(0);
      setAiMapResponse('جاري حساب المسار... توكلنا على الله');
      speakResponse(language === 'ar' ? 'تم تحديد الوجهة، يالله مشينا.' : 'Destination set. Let\'s go.');
  };

  const simulateIncomingCallSpeech = async () => {
      if (!selectedApp || selectedApp.id !== 'phone-translator' || callStatus !== 'active' || isAiSpeaking) return;
      
      setIsAiSpeaking(true);
      const script = MOCK_CALLER_SCRIPTS[Math.floor(Math.random() * MOCK_CALLER_SCRIPTS.length)];
      const dialect = selectedApp.config.dialect || config.dialect;
      const prompt = `Translate to Saudi ${dialect}: "${script}". Output ONLY translation.`;
      const translation = await generateResponse(prompt, dialect as any, [], [], language, config.apiKey);
      
      setCallTranscript(prev => [...prev, { original: script, translated: translation, isUser: false }]);
      
      const voice = selectedApp.config.voice || config.voice;
      const audio = await generateAudio(translation, voice, config.apiKey);
      if (audio) await audioService.playPCM(audio, config.speechSpeed);

      setIsAiSpeaking(false);
  };

  const runConversationLoop = async () => {
    if (!isSimulatingRef.current) return;

    if (selectedApp?.id === 'google-maps' && isNavigating && !isAiSpeaking) {
        if (Math.random() > 0.85) { 
            await handleNavigationEvent();
        }
    } else if (selectedApp?.id === 'phone-translator' && callStatus === 'active' && !isAiSpeaking) {
        if (Math.random() > 0.7) {
            await simulateIncomingCallSpeech();
        }
    } 
    
    if (isSimulatingRef.current) {
        setTimeout(runConversationLoop, 1500);
    }
  };

  useEffect(() => {
    if (isSimulating && activeTab === 'live') {
       const timer = setTimeout(() => {
          runConversationLoop();
       }, 1000);
       return () => clearTimeout(timer);
    }
  }, [isSimulating, activeTab, callStatus, isAiSpeaking, isNavigating, navStepIndex]);

  const handleConnect = async (appId: string) => {
     setIsConnecting(true);
     if (apps) {
         // Optimistic Update
         const targetApp = apps.find(a => a.id === appId);
         const newState = !targetApp?.connected;
         
         await IntegrationsService.toggleConnection(appId, newState);
         
         setApps(prev => prev ? prev.map(a => a.id === appId ? { ...a, connected: newState } : a) : []);
         if (selectedApp && selectedApp.id === appId) {
             setSelectedApp(prev => prev ? {...prev, connected: newState} : null);
         }
     }
     setIsConnecting(false);
  };

  const updateAppConfig = async (key: string, value: any) => {
     if (!selectedApp || !apps) return;
     const newConfig = { ...selectedApp.config, [key]: value };
     
     await IntegrationsService.updateConfig(selectedApp.id, newConfig);
     
     setApps(prev => prev ? prev.map(a => a.id === selectedApp.id ? { ...a, config: newConfig } : a) : []);
     setSelectedApp({ ...selectedApp, config: newConfig });
  };

  if (isLoading) return <div className="h-full flex items-center justify-center"><i className="fas fa-spinner fa-spin text-slate-500 text-2xl"></i></div>;

  return (
    <IntegrationsComponent 
      language={language}
      config={config}
      apps={visibleApps}
      selectedApp={selectedApp}
      setSelectedApp={setSelectedApp}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      isSimulating={isSimulating}
      setIsSimulating={setIsSimulating}
      isConnecting={isConnecting}
      handleConnect={handleConnect}
      updateAppConfig={updateAppConfig}
      userLocation={userLocation}
      activeDestination={activeDestination}
      isNavigating={isNavigating}
      isAiSpeaking={isAiSpeaking}
      aiMapResponse={aiMapResponse}
      navStepIndex={navStepIndex}
      mockRouteEvents={MOCK_ROUTE_EVENTS}
      destinationInput={destinationInput}
      setDestinationInput={setDestinationInput}
      startNavigation={startNavigation}
      callStatus={callStatus}
      setCallStatus={setCallStatus}
      callDuration={callDuration}
      callTranscript={callTranscript}
      formatTime={formatTime}
    />
  );
};

export default IntegrationsTemplate;
